/**
 * Lucas's rich-response contract.
 *
 * A turn is a short markdown answer plus, optionally, a few *blocks*: a video
 * from the help centre, a screenshot, a decision card, a copyable snippet, a
 * report to download.
 *
 * **The model emits references, never URLs.** Same rule already settled for the
 * Jira bundle: attached by the executor in code, not composed by the model. A
 * model that composes URLs invents them — and here it would invent a link into
 * our own S3, which is worse than an obviously broken one because it looks
 * legitimate. So a media block carries an `assetId`; the server is the only
 * thing allowed to turn that into a URL.
 *
 * The same reasoning splits `download` in two. Markdown is composed by the
 * model, because a diagnosis report *is* the answer, just longer than a bubble.
 * JSON is not: invented flow configuration looks valid, and someone will paste
 * it into a production flow. JSON must be generated server-side from the real
 * flow, so the model may only *ask* for it by `flowId`.
 *
 * This module is the pure contract — validate and normalise what the model
 * asked for. Resolving references into real assets happens in the API.
 */

import { nodeByType } from "../flow/nodeCatalog.js";
import { navItem } from "../platform/navigation.js";

export const BLOCK_TYPES = Object.freeze(
  new Set([
    "text",
    "image",
    "video",
    "decision",
    "snippet",
    "download",
    "action",
    "flow",
    "cards",
    "channels",
    "path",
  ]),
);

/**
 * Domínios cujos links podem virar botão.
 *
 * `action` é a única exceção à regra "o modelo não emite URL", e por isso ela é
 * fechada por allowlist: a URL vem do catálogo de erros ou da documentação da
 * plataforma, e um destino fora desta lista é descartado. Sem isso, o bloco de
 * ação seria a porta pela qual o modelo entregaria um link inventado com cara
 * de botão oficial.
 */
const ACTION_HOSTS = Object.freeze([
  "business.facebook.com",
  "developers.facebook.com",
  "www.facebook.com",
  "touchworks.com.br",
  "app.touchworks.com.br",
]);

function isAllowedActionUrl(url) {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return false;
    return ACTION_HOSTS.some(
      (host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`),
    );
  } catch {
    return false;
  }
}

/** Um turno é uma resposta, não uma página. */
const MAX_BLOCKS = 10;
const MAX_OPTIONS = 6;
const MAX_TEXT = 4000;
/** Acima disso o diagrama deixa de ser um caminho e vira uma parede. */
const MAX_STEPS = 12;
const DOWNLOAD_FORMATS = Object.freeze({ md: "text/markdown", json: "application/json" });

const str = (value, max = 500) =>
  typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;

/** Nome de arquivo é só nome: sem diretório, sem travessia. */
function safeFilename(value, fallbackExtension) {
  const base = String(value || "")
    .split(/[\\/]/)
    .pop()
    .replaceAll("..", "")
    .replaceAll(/[^a-zA-Z0-9._-]/g, "_")
    .slice(0, 80);
  if (!base || base === "." || base === "_") return `lucas.${fallbackExtension}`;
  return base.endsWith(`.${fallbackExtension}`) ? base : `${base}.${fallbackExtension}`;
}

/**
 * Um normalizador por tipo de bloco.
 *
 * Era um `switch` só, e cada tipo novo o engordava — chegou a 34 de
 * complexidade cognitiva. Uma função por tipo mantém cada regra do tamanho da
 * decisão que ela toma, e adicionar um bloco deixa de mexer nos outros.
 *
 * Toda entrada devolve o bloco pronto ou `null`. `null` é descarte: bloco que
 * não passa não aparece, em vez de aparecer quebrado.
 */
const NORMALIZERS = {
  text: (raw, fallbackText) => ({
    type: "text",
    text: str(raw.text, MAX_TEXT) || fallbackText,
    fallbackText,
  }),

  image: (raw, fallbackText) => normalizeMedia(raw, fallbackText, "image"),
  video: (raw, fallbackText) => normalizeMedia(raw, fallbackText, "video"),

  decision(raw, fallbackText) {
    const question = str(raw.question, 300);
    const options = (Array.isArray(raw.options) ? raw.options : [])
      .map((option) => {
        const label = str(option?.label, 60);
        if (!label) return null;
        return { label, message: str(option?.message, 300) || label };
      })
      .filter(Boolean)
      .slice(0, MAX_OPTIONS);

    if (!question || !options.length) return null;
    return { type: "decision", question, options, fallbackText };
  },

  action(raw, fallbackText) {
    const label = str(raw.label, 60);
    const url = str(raw.url, 2000);
    // Destino fora da allowlist é descartado: um botão com aparência oficial
    // apontando para lugar inventado é pior que nenhum botão.
    if (!label || !url || !isAllowedActionUrl(url)) return null;
    return { type: "action", label, url, fallbackText };
  },

  /**
   * Lista para escolher — preenchida pelo SERVIDOR.
   *
   * O modelo só declara a origem; `items` que ele mande é descartado. Sem isso
   * ele listaria fluxos plausíveis e inexistentes, que é a pior forma de errar
   * aqui: a pessoa vai procurar e não vai achar.
   */
  cards: (raw, fallbackText) =>
    raw.source === "flows" ? { type: "cards", source: "flows", fallbackText } : null,

  /**
   * Os canais da conta, para escolher em qual criar o template.
   *
   * Template de WhatsApp é aprovado por CANAL, não por conta: a tela só abre
   * com o canal escolhido. Por isso o destino não é uma página só — é uma por
   * canal, e sem a escolha não existe link para dar.
   *
   * Mesma regra dos cartões de fluxo: o modelo declara a intenção, o servidor
   * preenche os canais e monta cada URL. Ele não escreve endereço, então não
   * tem como escrever um que não abre.
   */
  channels: (raw, fallbackText) =>
    raw.purpose === "templates"
      ? { type: "channels", purpose: "templates", fallbackText }
      : null,

  /**
   * O modelo emite SÓ o tipo de cada passo. Rótulo, ícone e cor saem do
   * catálogo na hora de desenhar — mesma regra dos blocos de mídia: ele
   * trabalha com referência e nunca escreve o nome, então não tem como
   * escrevê-lo errado. Foi isso que produziu "adicione um nó input" quando na
   * tela está escrito "Aguardar Resposta".
   */
  flow(raw, fallbackText) {
    const steps = (Array.isArray(raw.steps) ? raw.steps : [])
      .map((step) => {
        const nodeType = str(step?.type, 60);
        // Nó fora do catálogo não vira desenho: mandaria a pessoa procurar na
        // biblioteca uma coisa que não existe lá.
        if (!nodeType || !nodeByType(nodeType)) return null;
        const note = str(step?.note, 120);
        return note ? { type: nodeType, note } : { type: nodeType };
      })
      .filter(Boolean)
      .slice(0, MAX_STEPS);

    if (!steps.length) return null;
    return { type: "flow", steps, fallbackText };
  },

  /**
   * O caminho na tela, com o ícone que a pessoa procura no menu.
   *
   * Mesma regra do bloco `flow`: o modelo emite só a CHAVE do item de menu, e
   * o rótulo e o ícone saem do catálogo de navegação na hora de desenhar. É por
   * isso que ele não consegue escrever "CRM" onde a tela diz "Kanban" — foi
   * exatamente esse erro que mandou alguém procurar um item inexistente.
   *
   * Passos livres (`text`) existem para o que não é item de menu: "abra o
   * pipeline", "aba Automações".
   */
  path(raw, fallbackText) {
    const steps = (Array.isArray(raw.path) ? raw.path : [])
      .map((step) => {
        const navKey = str(step?.nav, 40);
        // Chave fora do catálogo não vira passo: desenharia um ícone e um nome
        // que não existem no menu de ninguém.
        if (navKey) return navItem(navKey) ? { nav: navKey } : null;
        const text = str(step?.text, 80);
        return text ? { text } : null;
      })
      .filter(Boolean)
      .slice(0, MAX_STEPS);

    if (!steps.length) return null;
    return { type: "path", path: steps, fallbackText };
  },

  snippet(raw, fallbackText) {
    const code = str(raw.code, MAX_TEXT);
    if (!code) return null;
    return {
      type: "snippet",
      code,
      language: str(raw.language, 24),
      nodeType: str(raw.nodeType, 60),
      fallbackText,
    };
  },

  download(raw, fallbackText) {
    const format = DOWNLOAD_FORMATS[raw.format] ? raw.format : null;
    if (!format) return null;

    const filename = safeFilename(raw.filename, format);
    if (format === "json") return normalizeFlowDownload(raw, fallbackText, filename);

    const content = str(raw.content, 100_000);
    if (!content) return null;
    return { type: "download", format, filename, content, fallbackText };
  },
};

function normalizeMedia(raw, fallbackText, type) {
  const assetId = str(raw.assetId, 60);
  if (!assetId) return null;
  // `url` é deliberadamente ignorado, mesmo se vier preenchido.
  return { type, assetId, caption: str(raw.caption, 200), fallbackText };
}

/**
 * JSON só sai do servidor, nunca do modelo.
 *
 * Duas origens, ambas verificáveis: `flowId` exporta um fluxo que já existe na
 * conta; `source: "diagram"` monta o arquivo a partir dos passos que ele
 * declarou no bloco `flow` deste mesmo turno. Nos dois casos ele aponta para um
 * dado, em vez de escrever configuração — que sairia plausível e acabaria
 * importada numa conta de produção.
 */
function normalizeFlowDownload(raw, fallbackText, filename) {
  const flowId = str(raw.flowId, 60);
  if (flowId) return { type: "download", format: "json", filename, flowId, fallbackText };
  if (raw.source === "diagram") {
    return { type: "download", format: "json", filename, source: "diagram", fallbackText };
  }
  return null;
}

function normalizeBlock(raw) {
  if (!raw || typeof raw !== "object") return null;

  // Sem fallback o mesmo turno fica ilegível num cliente que não conhece o
  // bloco — ou no caminho do WhatsApp, onde não existe card nenhum.
  const fallbackText = str(raw.fallbackText, MAX_TEXT);
  if (!fallbackText) return null;

  // Tipo desconhecido vira texto com o fallback: um turno escrito por um modelo
  // mais novo continua legível numa tela antiga.
  const type = BLOCK_TYPES.has(raw.type) ? raw.type : "text";
  return NORMALIZERS[type](raw, fallbackText);
}

/**
 * @param {unknown} blocks — o que o modelo pediu
 * @returns {object[]} blocos válidos, normalizados e limitados
 */
export function normalizeBlocks(blocks) {
  if (!Array.isArray(blocks)) return [];
  return blocks.map(normalizeBlock).filter(Boolean).slice(0, MAX_BLOCKS);
}

/** MIME do download, para o Blob no cliente. */
export function downloadMimeType(format) {
  return DOWNLOAD_FORMATS[format] || "text/plain";
}

export default normalizeBlocks;

/**
 * O diagnóstico do fluxo, montado pela PLATAFORMA.
 *
 * Este bloco não vem do modelo — vem do validador, o mesmo que pinta os campos
 * de vermelho no editor. Existe porque a mesma pergunta, sobre o mesmo fluxo,
 * vinha voltando com respostas diferentes a cada tentativa: uma hora os dois
 * erros reais, outra hora "apontar o próximo nó para contact-update-1", outra
 * hora um loop infinito que não existia.
 *
 * O que varia pode ser a explicação; o que está errado, não. Fato tem forma
 * fixa, e forma fixa é a plataforma que desenha.
 *
 * @param {{errors?: object[], warnings?: object[]}} problemas
 * @returns {object|null}
 */
export function flowProblemsBlock({ errors = [], warnings = [] } = {}) {
  if (!errors.length && !warnings.length) return null;
  return {
    type: "problems",
    errors: errors.map((e) => ({ nodeId: e.nodeId || null, message: e.message })),
    warnings: warnings.map((w) => ({ nodeId: w.nodeId || null, message: w.message })),
    fallbackText: [...errors, ...warnings].map((p) => p.message).join(" · "),
  };
}
